import { ProductBarcode, ProductBarcodeType } from '@/types/entity/product';

// EWM-936 — parses the CT `barcodes` attribute into the flat, typed list the
// PDP specifications table renders.
//
// Shape verified live against electus-dev across all 8,928 published products:
//
//   {"ean_barcode":"9319236968291","upc_barcode":"","additional_barcodes":["…","…"]}
//
// Coverage of the 4,750 products whose value parses as JSON:
//   - `ean_barcode`         non-empty on all 4,750
//   - `upc_barcode`         non-empty on 126
//   - `additional_barcodes` non-empty on 544 (up to 9+ entries each)
//
// NOTE for reviewers: the ticket describes "inner, outer and carton" barcodes.
// No such types exist in the data — the source carries EAN, UPC and a flat
// `additional_barcodes` list (GS1 case codes), with no inner/outer/carton
// distinction to map. This maps what is actually there.
//
// The remaining 4,174 products hold the literal placeholder string
// `"testbarcodes"` rather than JSON. That is not parseable, so it arrives here
// as `undefined` and yields no rows — which is exactly the AC's "a product with
// no barcode data shows no barcode rows".

// Source key → the type we expose. A key absent from this map is IGNORED, so an
// unrecognised source type can never leak its raw key into the UI (AC: "a type
// with no mapping is handled without exposing a raw code").
const SINGLE_VALUE_KEYS: ReadonlyArray<readonly [string, ProductBarcodeType]> = [
  ['ean_barcode', 'ean'],
  ['upc_barcode', 'upc'],
];

const MULTI_VALUE_KEYS: ReadonlyArray<readonly [string, ProductBarcodeType]> = [['additional_barcodes', 'additional']];

// Present, a string, and not blank. Values themselves are never reformatted —
// the AC requires they display exactly as held in the source.
const isUsable = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;

export const mapProductBarcodes = (parsed: unknown): ProductBarcode[] => {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return [];
  const source = parsed as Record<string, unknown>;

  const out: ProductBarcode[] = [];
  // Deduped by value, first-wins. `additional_barcodes[0]` repeats the
  // `ean_barcode` on most products that carry both (e.g. PP0708, HM9505,
  // TL4260), and listing the same number twice under two different type
  // labels reads as a data error. EAN is emitted first, so it keeps the value.
  const seen = new Set<string>();

  const push = (type: ProductBarcodeType, value: string) => {
    if (seen.has(value)) return;
    seen.add(value);
    out.push({ type, value });
  };

  for (const [key, type] of SINGLE_VALUE_KEYS) {
    const value = source[key];
    if (isUsable(value)) push(type, value);
  }

  for (const [key, type] of MULTI_VALUE_KEYS) {
    const values = source[key];
    if (!Array.isArray(values)) continue;
    for (const value of values) if (isUsable(value)) push(type, value);
  }

  return out;
};
